import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDevelopedTemplates } from '../../../modules/selectors/templates';
import { fetchTemplates } from '../../../modules/actions';
import { setModalOpenState } from '../../../actions';
import { ITemplate } from '../../../modules/reducers';

/** Pointer travel past which a press counts as a drag rather than a click. */
const DRAG_THRESHOLD = 5;
const SCROLL_STEP = 300;

interface IArgs {
  selectedTemplateId: string;
  onSelect: (templateId: string) => void;
}

interface ITemplatesList {
  templates: ITemplate[];
  scrollRef: React.RefObject<HTMLDivElement>;
  isSelected: (templateId: string) => boolean;
  selectTemplate: (templateId: string) => void;
  scroll: (direction: 'left' | 'right') => void;
  dragHandlers: {
    onMouseDown: (e: React.MouseEvent) => void;
    onMouseMove: (e: React.MouseEvent) => void;
    onMouseUp: () => void;
    onMouseLeave: () => void;
  };
}

/**
 * Data and drag-to-scroll behaviour for the template carousel.
 *
 * The carousel is scrolled by dragging, so a press that moved is swallowed rather
 * than treated as a selection — otherwise every drag would also pick a template.
 */
export const useTemplatesList = ({ selectedTemplateId, onSelect }: IArgs): ITemplatesList => {
  const dispatch = useDispatch();
  const templates = useSelector(getDevelopedTemplates);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const startX = useRef(0);
  const scrollLeftStart = useRef(0);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (!scrollRef.current || !selectedTemplateId || !templates.length) {
      return;
    }

    const selectedIndex = templates.findIndex(({ _id }) => _id === selectedTemplateId);
    const selectedElement = scrollRef.current.children[selectedIndex] as HTMLElement | undefined;

    selectedElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [templates, selectedTemplateId]);

  const endDrag = useCallback(() => {
    isDragging.current = false;

    if (scrollRef.current) {
      scrollRef.current.style.scrollBehavior = 'smooth';
      scrollRef.current.style.cursor = '';
    }
  }, []);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollRef.current) {
      return;
    }

    isDragging.current = true;
    hasDragged.current = false;
    startX.current = e.pageX;
    scrollLeftStart.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.scrollBehavior = 'auto';
    scrollRef.current.style.cursor = 'grabbing';
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) {
      return;
    }

    const dx = e.pageX - startX.current;

    if (Math.abs(dx) > DRAG_THRESHOLD) {
      hasDragged.current = true;
    }

    scrollRef.current.scrollLeft = scrollLeftStart.current - dx;
  }, []);

  const selectTemplate = useCallback(
    (templateId: string) => {
      if (hasDragged.current) {
        return;
      }

      const template = templates.find(({ _id }) => _id === templateId);

      if (template?.userIsAllowedToUseTemplate) {
        onSelect(templateId);

        return;
      }

      dispatch(
        setModalOpenState({
          key: 'upgradeModal',
          state: true,
          modalData: { addon: 'template', templateId }
        })
      );
    },
    [templates, onSelect, dispatch]
  );

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += direction === 'left' ? -SCROLL_STEP : SCROLL_STEP;
    }
  }, []);

  const isSelected = useCallback(
    (templateId: string) => selectedTemplateId === templateId,
    [selectedTemplateId]
  );

  return {
    templates,
    scrollRef,
    isSelected,
    selectTemplate,
    scroll,
    dragHandlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp: endDrag,
      onMouseLeave: endDrag
    }
  };
};

export default useTemplatesList;
